import { Bone, Sunrise, Moon, HeartPulse } from "lucide-react";
import { Reveal, Label } from "./motion";
import { IMAGES } from "@/data/content";

const CARDS = [
  {
    icon: Bone,
    title: "Stiff Joints & Stairs",
    body: "Hesitating before the couch, slower to rise, or pausing at the bottom of the stairs.",
    testid: "cbdoodle-problem-card-joints",
  },
  {
    icon: Sunrise,
    title: "Slower Morning Mobility",
    body: "That first stretch of the day takes longer, and walks feel a little less springy.",
    testid: "cbdoodle-problem-card-mobility",
  },
  {
    icon: Moon,
    title: "Nighttime Restlessness",
    body: "Pacing, repositioning, or waking through the night instead of settling into deep rest.",
    testid: "cbdoodle-problem-card-restless",
  },
  {
    icon: HeartPulse,
    title: "Mild Anxiety & Unease",
    body: "More clingy, startled by storms, or unsettled when routines shift with age.",
    testid: "cbdoodle-problem-card-anxiety",
  },
];

export const Problem = () => (
  <section id="problem" data-testid="cbdoodle-problem-container" className="bg-[#F3EDE2] px-5 py-20 sm:px-8 lg:py-28">
    <div className="mx-auto max-w-7xl">
      <div className="grid gap-10 lg:grid-cols-12 lg:gap-14">
        <div className="lg:col-span-5">
          <Reveal><Label>The Golden Years</Label></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-4 font-serif text-3xl font-semibold leading-tight text-[#2C2B29] sm:text-4xl lg:text-[2.9rem]">
              Aging shows up quietly — in the little pauses.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-5 max-w-md font-sans leading-relaxed text-[#5C5850]">
              Poodles are bright, spirited companions well into their senior years. But time asks more of their
              joints, sleep, and calm. Noticing these small changes early is the first act of love.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-8 overflow-hidden rounded-[1.75rem] border border-[#E6E0D4] shadow-sm">
              <img
                src={IMAGES.problem}
                alt="Senior poodle resting comfortably at home"
                className="h-64 w-full object-cover sm:h-72"
              />
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <div className="grid gap-5 sm:grid-cols-2">
            {CARDS.map((c, i) => {
              const Icon = c.icon;
              return (
                <Reveal key={c.title} delay={i * 0.08} data-testid={c.testid}>
                  <div className="group h-full rounded-[1.5rem] border border-[#E6E0D4] bg-[#FFFDF9] p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
                    <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#E2ECE7] text-[#4A6B5D] transition-colors group-hover:bg-[#4A6B5D] group-hover:text-white">
                      <Icon size={22} />
                    </div>
                    <h3 className="font-serif text-xl font-semibold text-[#2C2B29]">{c.title}</h3>
                    <p className="mt-2.5 font-sans text-[15px] leading-relaxed text-[#5C5850]">{c.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  </section>
);
